import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './GetVerified.module.sass';
import InfoPage from '../InfoPage';

// Same scalloped badge-check seal used by CollabBadge (lucide "badge-check"),
// rendered large for the hero and small for card accents.
const BADGE_PATH = 'M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z';
const CHECK_PATH = 'm9 12 2 2 4-4';

const VerifiedStamp = ({ size = 72 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ display: 'block', flexShrink: 0 }}>
    <path d={BADGE_PATH} fill="none" stroke="#ffffff" strokeWidth="5" strokeLinejoin="round" />
    <path d={BADGE_PATH} fill="#dc1d08" stroke="#dc1d08" strokeWidth="1.5" strokeLinejoin="round" />
    <path d={CHECK_PATH} fill="none" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Each path gets its own brand color ("color chunking") so the three
// steps read as distinct blocks: navy, white, red.
const PATHS = [
  {
    number: 1,
    tone: 'toneNavy',
    title: 'Be a Founding Member',
    description: 'Our founding members have a proven track record for successful collaborations and were vetted prior to the Postaway launch. This distinction was by invitation only, and we appreciate them believing in Postaway before we were an official platform. Every Founding Member of Postaway is Verified.',
  },
  {
    number: 2,
    tone: 'toneWhite',
    title: 'Receive a 5-Star Review',
    description: 'Receive a 5-star review from a Postaway creator or host you\u2019ve recently partnered with or have worked with in the past. This can be from a previous collaboration, including one that took place before joining Postaway (as long as both creator and host are current members).',
    hint: 'To review a host or creator you\u2019ve previously worked with, head to your dashboard and select \u201cLeave a Review.\u201d',
    cta: { label: 'Leave a Review', to: '/dashboard' },
  },
  {
    number: 3,
    tone: 'toneRed',
    title: 'Complete a Postaway Collab',
    description: 'Complete a collaboration through Postaway using our Collab Tracker, finished off with a 5-star review. Every completed collab builds your track record on the platform and establishes trust with future partners. If you found a collaboration partner through the platform, but did not use the Collab Tracker, that\u2019s okay. You can retroactively \u2018Explore a Collab\u2019 (under Collabs tab) with the same partner and leave a review there as well. This will qualify you for a verified stamp.',
    cta: { label: 'Browse Collab Opportunities', to: '/collab-board' },
  },
];

const GetVerified = () => {
  const navigate = useNavigate();

  const content = (
    <div className={styles.page}>
      <div className={styles.board}>
        <div className={styles.inner}>
          <div className={styles.hero}>
            <VerifiedStamp size={72} />
            <h1 className={styles.title}>Earn Your Verified Stamp</h1>
            <p className={styles.subtitle}>
              Establish trust with future collaboration partners by becoming verified on Postaway.
              A verified stamp on your profile shows that you are a vetted member, have collaborated
              successfully in the past and are easy to work with. Members can be verified one of
              three ways on this platform.
            </p>
          </div>

          <div className={styles.divider}>
            <span className={styles.dividerLabel}>3 Ways to Get Verified</span>
          </div>

          <div className={styles.paths}>
            {PATHS.map((path) => (
              <div key={path.number} className={`${styles.card} ${styles[path.tone]}`}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardNumber}>{path.number}</div>
                  <h2 className={styles.cardTitle}>{path.title}</h2>
                </div>
                <div className={styles.cardBody}>
                  <p className={styles.cardText}>{path.description}</p>
                  {path.hint && <p className={styles.cardHint}>{path.hint}</p>}
                  {path.cta && (
                    <button
                      type="button"
                      className={styles.cardCta}
                      onClick={() => navigate(path.cta.to)}
                    >
                      {path.cta.label}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.footerNote}>
            <VerifiedStamp size={22} />
            <p>
              No application needed — once you qualify through any of these paths, the verified
              stamp appears on your profile automatically.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <InfoPage
      title="Get Verified"
      divider={false}
      below={content}
      disableStamps
      tallOverride={false}
      customMinHeight={undefined}
    />
  );
};

export default GetVerified;
